import { MODULES, PERMISSION_LEVELS, type Module, type PermissionLevel, type UserType } from "./enums.ts";

const LEVEL_RANK: Record<PermissionLevel, number> = { none: 0, read: 1, write: 2, approve: 3, admin: 4 };

export function levelRank(level: PermissionLevel): number {
  return LEVEL_RANK[level];
}

/** True when the granted level is at least the required one. approve includes write includes read. */
export function hasLevel(granted: PermissionLevel | undefined | null, required: PermissionLevel): boolean {
  if (required === "none") return true;
  if (!granted) return false;
  return LEVEL_RANK[granted] >= LEVEL_RANK[required];
}

export type PermissionGrid = Partial<Record<Module, PermissionLevel>>;

export function fullGrid(level: PermissionLevel): PermissionGrid {
  return Object.fromEntries(MODULES.map((m) => [m, level])) as PermissionGrid;
}

export function isPermissionLevel(v: unknown): v is PermissionLevel {
  return typeof v === "string" && (PERMISSION_LEVELS as readonly string[]).includes(v);
}

/**
 * Shipped default permission grids (docs/MODULES/platform.md → "Shipped defaults").
 * Roles are data — these values are seeded into role_permissions and are
 * editable by an administrator afterwards.
 */
export const DEFAULT_ROLE_GRIDS: Record<string, { name: string; userType: UserType; grid: PermissionGrid }> = {
  admin: { name: "Administrator", userType: "internal", grid: fullGrid("admin") },
  rc: {
    name: "Rabbinical Coordinator",
    userType: "internal",
    grid: {
      ...fullGrid("approve"),
      billing: "read",
      accounts_payable: "write",
      settings: "read",
      reports: "read",
      shechita: "read",
      directory: "write",
    },
  },
  accounting: {
    name: "Accounting",
    userType: "internal",
    grid: {
      ...fullGrid("none"),
      stam: "read",
      wine: "read",
      esrogim: "read",
      companies: "read",
      locations: "read",
      productions: "read",
      inspections: "read",
      events: "read",
      certificates: "read",
      billing: "admin",
      accounts_payable: "admin",
      reports: "read",
    },
  },
  ingredient_reviewer: {
    name: "Ingredient reviewer",
    userType: "internal",
    grid: { ...fullGrid("read"), ingredients: "approve", products: "approve", settings: "none" },
  },
  directory_team: {
    name: "מפתח team",
    userType: "internal",
    grid: { ...fullGrid("none"), directory: "approve", products: "read", companies: "read", reports: "read" },
  },
  reporting: {
    name: "Reporting",
    userType: "internal",
    grid: { ...fullGrid("read"), settings: "none" },
  },
  company_user: {
    name: "Company user",
    userType: "company",
    grid: {
      ...fullGrid("none"),
      companies: "write",
      locations: "write",
      ingredients: "write",
      products: "write",
      productions: "write",
      wine: "read",
      inspections: "read",
      certificates: "read",
      billing: "write",
      holograms: "write",
      esrogim: "read",
    },
  },
  mashgiach: {
    name: "Mashgiach",
    userType: "mashgiach",
    grid: {
      ...fullGrid("none"),
      companies: "read",
      locations: "read",
      inspections: "write",
      productions: "read",
      events: "read",
      ingredients: "write",
      accounts_payable: "write",
      holograms: "write",
      esrogim: "approve",
    },
  },
  facility_user: {
    name: "Facility user",
    userType: "location_user",
    grid: { ...fullGrid("none"), locations: "read", inspections: "read", holograms: "write", shechita: "write" },
  },
};

/** Global search is staff-facing only. Structural, not configurable. */
export function canUseGlobalSearch(userType: UserType): boolean {
  return userType === "internal";
}
